"use client";

import { UserCircle, RefreshCw } from "lucide-react";
import { useState } from "react";

import { editsPatientChartNumber } from "@/configuration/ehr/chart-number";
import { normalizeIdentifiers } from "@/configuration/mrn-primary";
import { Field } from "@/src/components/cards/common";
import EditableCard from "@/src/components/cards/editable-card";
import FormField from "@/src/components/form-field";
import { Button } from "@/src/components/shadcn/ui/button";
import GeocodeLookupSection from "@/src/components/tasks/intake-sections/geocode-lookup-section";
import { useUser } from "@/src/context/user-context";
import { toast } from "@/src/hooks/use-toast";
import { useCreateOrUpdatePatientMutation } from "@/src/lib/data-fetchers/patients-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { tryGetEhrSystemId, tryGetMrn } from "@/src/lib/resolve-ehr-ids";
import { convertEmptyStringValuesToNull, getNestedValue } from "@/src/lib/utils/object-utils";
import { compareStrings, getDisplayValue } from "@/src/lib/utils/string-utils";
import { type Address } from "@/src/models/address";
import { Patient, PatientErrors, PatientFieldKey, PatientType, validatePatient } from "@/src/models/patient";
import { PersonEthnicity, PersonGender, PersonLanguage } from "@/src/models/person";

// Enhanced FormField component with conflict detection
interface FormFieldWithConflictProps {
  fieldKey: string;
  hasConflict: (fieldKey: string) => boolean;
  getSecondaryValue: (fieldKey: string) => string | null;
  handleInputChange: (field: PatientFieldKey, value: string) => void;
}

const FormFieldWithConflict = ({
  fieldKey,
  hasConflict,
  getSecondaryValue,
  handleInputChange,
  ...props
}: FormFieldWithConflictProps & React.ComponentProps<typeof FormField>) => {
  const conflict = hasConflict(fieldKey);
  const secondaryValue = getSecondaryValue(fieldKey);

  return (
    <div className="space-y-1">
      <FormField {...props} className={conflict ? "bg-warn/10 border-warn/30" : ""} />
      {conflict && secondaryValue && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="text-warn">EMR: {secondaryValue}</span>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            className="h-6 text-xs px-2 py-0 hover:bg-muted rounded-full flex items-center justify-center"
            onClick={(e) => {
              e.preventDefault();

              handleInputChange(fieldKey as PatientFieldKey, secondaryValue);
            }}
          >
            Switch
            <RefreshCw className="ml-1 h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};

// List of fields to validate for this card
const patientFieldsToValidate: (keyof Patient)[] = [
  "givenName",
  "middleName",
  "familyName",
  "ethnicity",
  "race",
  "language",
  "dateOfBirth",
  "gender",
  "homePhoneNumber",
  "mobilePhoneNumber",
  "personalEmail",
];
const fieldsToValidate: PatientFieldKey[] = [...patientFieldsToValidate];

interface PatientCardEditableProps {
  patient: Patient;
  onClose: () => void;
  // Called after a successful create with the persisted patient (which carries
  // the server-assigned displayId). Falls back to onClose when not provided.
  onCreated?: (patient: Patient) => void;
  createMode?: boolean;
  compareMode?: boolean;
  secondaryObject?: Patient | null;
  fields?: Field[];
}

export default function PatientCardEditable({
  patient,
  onClose,
  onCreated,
  createMode = false,
  compareMode = false,
  secondaryObject = null,
  fields = [],
}: PatientCardEditableProps) {
  const { group } = useUser();
  const editChartNumber = Boolean(!createMode && group && editsPatientChartNumber(group));
  const [formData, setFormData] = useState<Patient>({
    ...patient,
  });
  const [errors, setErrors] = useState<PatientErrors>({});

  // Helper function to check if field has conflict with secondary object
  const hasConflict = (fieldKey: string) => {
    if (!compareMode || !secondaryObject) return false;

    const currentValue = getNestedValue(formData, fieldKey);
    const secondaryValue = getNestedValue(secondaryObject, fieldKey);

    const field = fields.find((f: Field) => f.key === fieldKey);
    const fieldType = field?.type ?? "text";

    return !compareStrings(currentValue || "", secondaryValue || "", fieldType);
  };

  // Helper function to get secondary value for switch functionality
  const getSecondaryValue = (fieldKey: string) => {
    if (!compareMode || !secondaryObject) return null;

    const val = getNestedValue(secondaryObject, fieldKey);
    const field = fields.find((f: Field) => f.key === fieldKey);
    const fieldType = field?.type ?? "text";

    return getDisplayValue(val, fieldType);
  };

  // Helper function to get field props based on field configuration
  const getFieldProps = (field: Field) => {
    if (field.key === "ehrId" && editChartNumber && group) {
      return {
        label: "Chart ID (MRN)",
        value: tryGetMrn(formData, group) ?? "",
        onChange: (value: string) => {
          setFormData((prev) => ({
            ...prev,
            ...normalizeIdentifiers(group, tryGetEhrSystemId(prev, group), value.trim() || null),
          }));
        },
      };
    }

    const baseProps = {
      label: field.label,
      value: getNestedValue(formData, field.key) || "",
      onChange: (value: string) => handleInputChange(field.key as PatientFieldKey, value),
      error: getNestedValue(errors, field.key),
      required: field.required,
    };

    // Add type-specific props
    switch (field.key) {
      case "dateOfBirth":
        return { ...baseProps, type: "date" as const };
      case "homePhoneNumber":
      case "mobilePhoneNumber":
        return {
          ...baseProps,
          type: "tel" as const,
          placeholder: "555-123-4567",
        };
      case "personalEmail":
        return { ...baseProps, type: "email" as const };
      case "gender":
        return {
          ...baseProps,
          type: "select" as const,
          options: Object.values(PersonGender).map((gender) => ({
            value: gender,
            label: gender,
          })),
        };
      case "ethnicity":
        return {
          ...baseProps,
          type: "select" as const,
          options: Object.values(PersonEthnicity).map((ethnicity) => ({
            value: ethnicity,
            label: ethnicity,
          })),
        };
      case "language":
        return {
          ...baseProps,
          type: "select" as const,
          options: Object.values(PersonLanguage).map((language) => ({
            value: language,
            label: language,
          })),
        };
      case "homeAddress?.line2":
        return { ...baseProps, placeholder: "Apt, Suite, etc." };
      case "type":
        return {
          ...baseProps,
          type: "select" as const,
          options: Object.values(PatientType).map((type) => ({
            value: type,
            label: type,
          })),
        };
      default:
        return baseProps;
    }
  };

  const createOrUpdatePatientMutation = useCreateOrUpdatePatientMutation();

  const handleAddressUpdate = (_key: string, value: Address | null) => {
    setFormData((prev) => ({
      ...prev,
      homeAddress: value,
    }));

    if (errors.homeAddress) {
      setErrors((prev) => ({
        ...prev,
        homeAddress: undefined,
      }));
    }
  };

  const handleInputChange = (field: PatientFieldKey, value: string) => {
    const patientField = field as keyof Patient;

    setFormData((prev) => ({
      ...prev,
      [patientField]: value,
    }));

    // Clear error for this field when user starts typing
    setErrors((prev) => {
      const { [patientField]: _, ...rest } = prev;

      return rest;
    });
  };

  const handleSave = async () => {
    const processedFormData = convertEmptyStringValuesToNull(formData);
    const { isValid, errors } = validatePatient(processedFormData, fieldsToValidate);

    // A patient needs at least a name to be meaningful; the schema treats these
    // as nullable, so enforce presence here.
    if (!processedFormData.givenName) errors.givenName = "Given Name is required";
    if (!processedFormData.familyName) errors.familyName = "Family Name is required";

    setErrors(errors);

    if (!isValid || errors.givenName || errors.familyName) {
      return;
    }

    try {
      const savedPatient = await createOrUpdatePatientMutation.mutateAsync(processedFormData);

      if (createMode && onCreated) {
        onCreated(savedPatient);
      } else {
        onClose?.();
      }

      toast({
        title: createMode ? "Patient created" : "Patient updated",
        description: createMode
          ? "The patient was successfully created."
          : "The patient's information was successfully saved.",
        variant: "default",
      });
    } catch (error) {
      logger.error("Error saving patient:", { error });
    }
  };

  const addressFieldKeys = [
    "homeAddress.line1",
    "homeAddress.line2",
    "homeAddress.city",
    "homeAddress.state",
    "homeAddress.postalCode",
  ];
  const nonAddressFields = fields.filter((field) => !addressFieldKeys.includes(field.key));
  const hasAddressFields = fields.some((field) => addressFieldKeys.includes(field.key));

  return (
    <EditableCard
      title={createMode ? "Create New Patient" : "Edit Patient"}
      icon={UserCircle}
      initialData={patient}
      onCancel={onClose}
      onSave={handleSave}
      isSaving={createOrUpdatePatientMutation.isPending}
      createMode={createMode}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-x-4 gap-y-4 text-sm">
          {nonAddressFields.map((field) => {
            const fieldProps = getFieldProps(field);
            const isComparable = field.comparable && compareMode;

            return (
              <div key={field.key} className={field.colSpan === 2 ? "col-span-2" : ""}>
                {isComparable ? (
                  <FormFieldWithConflict
                    fieldKey={field.key}
                    hasConflict={hasConflict}
                    getSecondaryValue={getSecondaryValue}
                    handleInputChange={handleInputChange}
                    {...fieldProps}
                  />
                ) : (
                  <FormField {...fieldProps} />
                )}
              </div>
            );
          })}
        </div>
        {hasAddressFields && <GeocodeLookupSection address={formData.homeAddress} onUpdate={handleAddressUpdate} />}
      </div>
    </EditableCard>
  );
}
